import { useEffect, useState } from "react";
import Chart from "react-apexcharts";

const ColumnChart = ({ billingCycles, kit_number }) => {
	const [currentBillingCycle, setCurrentBillingCycle] = useState(null);
	const [selectedMonth, setSelectedMonth] = useState("thisMonth");
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);

	useEffect(() => {
		const currentDate = new Date();
		const currentMonth = currentDate.getUTCMonth() + 1; // UTC month (0-11), so add 1 for 1-indexing
		const previousMonth = currentMonth === 1 ? 12 : currentMonth - 1;
		const year =
			currentMonth === 1
				? currentDate.getUTCFullYear() - 1 // Previous month falls in the last year
				: currentDate.getUTCFullYear(); // Current year

		const findBillingCycle = (month, year) =>
			billingCycles.find((cycle) => {
				const cycleMonth = new Date(cycle.startDate).getUTCMonth() + 1;
				const cycleYear = new Date(cycle.startDate).getUTCFullYear();
				return month === cycleMonth && year === cycleYear;
			});

		const currentCycle =
			selectedMonth === "thisMonth"
				? findBillingCycle(currentMonth, currentDate.getUTCFullYear())
				: findBillingCycle(previousMonth, year);

		setCurrentBillingCycle(currentCycle);
	}, [billingCycles, selectedMonth]);

	const getDatesInRange = (startDate, endDate) => {
		const adjustedEndDate = new Date(endDate);
		if (adjustedEndDate.getUTCDate() === 1) {
			adjustedEndDate.setUTCDate(adjustedEndDate.getUTCDate() - 1);
		}

		adjustedEndDate.setUTCHours(0, 0, 0, 0);

		const dates = [];
		const currentDate = new Date(startDate);
		currentDate.setUTCHours(0, 0, 0, 0);

		while (currentDate <= adjustedEndDate) {
			dates.push(new Date(currentDate));
			currentDate.setUTCDate(currentDate.getUTCDate() + 1);
		}

		return dates;
	};
	const startDate = new Date(currentBillingCycle?.startDate);
	const endDate = new Date(currentBillingCycle?.endDate);
	const allDates = getDatesInRange(startDate, endDate);

	const today = new Date().toLocaleDateString(); // Get today's date as string

	const data = allDates.map((date) => {
		const dateString = date.toISOString().split("T")[0];
		const usageData = currentBillingCycle?.dailyDataUsage.find(
			(usage) => usage.date.split("T")[0] === dateString,
		);

		const priorityGB = usageData?.newServiceLine
			? usageData.newServiceLine.priorityGB
			: usageData?.priorityGB || 0;
		const standardGB = usageData?.newServiceLine
			? usageData.newServiceLine.standardGB
			: usageData?.standardGB || 0;

		return {
			x: date.toLocaleDateString(),
			priorityGB,
			standardGB,
			isTodayPriorityGB: date.toLocaleDateString() === today && priorityGB, // Track if it's today for priorityGB
			isTodaystandardGB: date.toLocaleDateString() === today && standardGB, // Track if it's today for standardGB
		};
	});

	const prioritySeries = data.map((point) => (point.isTodayPriorityGB ? null : point.priorityGB));
	const priorityTodaySeries = data.map((point) =>
		point.isTodayPriorityGB ? point.priorityGB : null,
	);
	const standardSeries = data.map((point) => (point.isTodaystandardGB ? null : point.standardGB));
	const standardTodaySeries = data.map((point) =>
		point.isTodaystandardGB ? point.standardGB : null,
	);

	const chartOptions = {
		chart: {
			type: "bar",
			height: "320px",
			fontFamily: "Inter, sans-serif",
			toolbar: { show: false },
		},
		tooltip: {
			shared: true,
			intersect: false,
			style: {
				fontFamily: "Inter, sans-serif",
				cssClass: "text-xs font-normal fill-gray-500 fill-gray-400",
			},
			theme: "dark",
		},
		states: {
			hover: {
				filter: {
					type: "darken",
					value: 1,
				},
			},
		},
		stroke: {
			show: true,
			width: 0,
			colors: ["transparent"],
		},
		grid: {
			show: false,
			strokeDashArray: 4,
			padding: { left: 2, right: 2, top: -14 },
		},
		dataLabels: { enabled: false },
		legend: {
			show: true,
			showForSingleSeries: true,
			position: "top",
			horizontalAlign: "right",
			onItemClick: {
				toggleDataSeries: false,
			},
			onItemHover: {
				highlightDataSeries: false,
			},
		},
		xaxis: {
			floating: false,
			labels: {
				show: true,
				style: {
					fontFamily: "Inter, sans-serif",
					fontSize: "12px",
					fontWeight: "bold",
					colors: "#FFFFFF",
				},
			},
			axisBorder: { show: true, color: "#FFFFFF" },
			axisTicks: { show: true, color: "#FFFFFF" },
		},
		yaxis: {
			labels: {
				style: {
					fontFamily: "Inter, sans-serif",
					fontSize: "12px",
					fontWeight: "bold",
					colors: "#FFFFFF",
				},
			},
		},
		fill: { opacity: 1 },
		colors: ["#1A56DB"], // Default color
		plotOptions: {
			bar: {
				horizontal: false,
				columnWidth: "100%",
				borderRadiusApplication: "end",
				borderRadius: 6,
			},
		},
	};

	const series = [
		{
			name: "Priority GB",
			color: "#1A56DB", // bleu
			data: data.map((point, index) => ({
				x: point.x,
				y: prioritySeries[index],
			})),
		},
		{
			name: "Priority GB Today",
			color: "#51e89d", // vert fluo
			data: data.map((point, index) => ({
				x: point.x,
				y: priorityTodaySeries[index],
			})),
		},
		{
			name: "Standard GB",
			color: "#ed5474", // rose
			data: data.map((point, index) => ({
				x: point.x,
				y: standardSeries[index],
			})),
		},
		{
			name: "Standard GB Today",
			color: "#feb527", // jaune
			data: data.map((point, index) => ({
				x: point.x,
				y: standardTodaySeries[index],
			})),
		},
	];

	const toggleDropdown = () => setIsDropdownOpen(!isDropdownOpen);
	const handleSelectMonth = (month) => {
		setSelectedMonth(month);
		setIsDropdownOpen(false);
	};

	return (
		<div>
			<div className="relative flex items-center">
				<button
					onClick={toggleDropdown}
					className="inline-flex min-h-[40px] items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-blue-gray px-3 font-medium text-light-white text-xs transition-all duration-200 hover:bg-dark-blue focus:outline-none focus:ring-4 focus:ring-dark-blue sm:min-h-[44px] sm:px-4 sm:text-sm md:px-5 md:text-base"
					type="button"
				>
					{selectedMonth === "thisMonth" ? "This Month" : "Last Month"}
					<svg
						className="h-3 w-3 shrink-0 text-white sm:h-4 sm:w-4 md:h-4 md:w-4"
						aria-hidden="true"
						xmlns="http://www.w3.org/2000/svg"
						fill="none"
						viewBox="0 0 10 6"
					>
						<path
							stroke="currentColor"
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth="2"
							d="m1 1 4 4 4-4"
						/>
					</svg>
				</button>

				{isDropdownOpen && (
					<div
						id="dropdown"
						className="absolute left-0 z-10 mt-36 w-36 divide-y divide-gray-100 rounded-lg bg-dark-blue shadow"
					>
						{" "}
						<ul className="py-2 text-gray-200 text-sm">
							<li>
								<a
									href="#"
									onClick={() => handleSelectMonth("thisMonth")}
									className="block px-2 py-2 hover:bg-blue-gray"
								>
									This Month
								</a>
							</li>
							<li>
								<a
									href="#"
									onClick={() => handleSelectMonth("lastMonth")}
									className="block px-2 py-2 hover:bg-blue-gray"
								>
									Last Month
								</a>
							</li>
						</ul>
					</div>
				)}
			</div>

			<Chart options={chartOptions} series={series} type="bar" height="320px" />

			<span className="text-red-500">
				Data usage tracking may be delayed by 24 hours or more. Refer to your statement for accurate
				billing information.
			</span>
		</div>
	);
};

export default ColumnChart;
